import { ACCENT2, BG, BORDER, CARD, DARK, DIM, MID } from "../theme";
import { supabase } from "../lib/supabase";
import { fetchAllRows } from "../lib/fetchAllRows";
import { isMissingAccountColumn, isMissingTable } from "../lib/dbErrors";
import { useEffect, useState } from "react";
import { formatMoney } from "../money";
import { describeSortingCheck } from "../sortingCheckText";
import { planRuleChanges, summarizeLaborSources } from "../laborRules";
import { EXPENSE_CATEGORIES } from "../lib/constants";
import { isDemoPath } from "../demoMode";
import { lastSyncedText, syncProblemsText } from "../syncStatusText";

// ─── VENDOR SETUP MODAL ──────────────────────────────────────────────────────
// Shown after first sync (or via sidebar) to let the user pick which vendors
// are job-related. Future syncs only process those vendors, eliminating bank noise.

// ─── LABOR SECTION ───────────────────────────────────────────────────────────
// Which costs count as labor. Everything else counts as materials. Saved per business in labor_rules.
const LABOR_SAVE_ERROR = "We couldn't save that. Please try again.";

function LaborPanel({ userId, onSave, onClose, fromQuickBooks = true }) {
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [summary, setSummary]   = useState(null);
  const [rules, setRules]       = useState([]);
  const [acctChoice, setAcctChoice]     = useState({}); // lower-case name -> true/false
  const [vendorChoice, setVendorChoice] = useState({}); // lower-case name -> 'labor' | 'not_labor' | 'auto'
  const [saving, setSaving]     = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        // Before the labor update is applied the account_name column does not exist: read without it.
        // Every page is read, so a long history is never cut short. If any page fails, the error shows instead of partial totals.
        let txnRes = await fetchAllRows(() => supabase.from('transactions').select('id, type, vendor, amount, account_name').eq('contractor_id', userId).eq('type', 'expense'));
        if (isMissingAccountColumn(txnRes.error)) txnRes = await fetchAllRows(() => supabase.from('transactions').select('id, type, vendor, amount').eq('contractor_id', userId).eq('type', 'expense'));
        let inboxRes = await fetchAllRows(() => supabase.from('inbox_tags').select('id, account_name').eq('contractor_id', userId));
        if (isMissingAccountColumn(inboxRes.error)) inboxRes = { data: [] };
        if (inboxRes.error) throw inboxRes.error;
        const rulesRes = await fetchAllRows(() => supabase.from('labor_rules').select('*').eq('contractor_id', userId));
        // Cash costs added in Canopy. If their table isn't set up yet there are none to list.
        const cashRes = await fetchAllRows(() => supabase.from('manual_expenses').select('id, vendor, amount, category, job_id').eq('contractor_id', userId));
        if (cancelled) return;
        if (cashRes.error && !isMissingTable(cashRes.error)) throw cashRes.error;
        if (txnRes.error) throw txnRes.error;
        if (rulesRes.error && !isMissingTable(rulesRes.error)) throw rulesRes.error;
        const savedRules = rulesRes.error ? [] : (rulesRes.data || []);
        const reviewAccounts = {};
        (inboxRes.data || []).forEach(r => { if (r.account_name) reviewAccounts[r.id] = r.account_name; });
        const sum = summarizeLaborSources({ transactions: txnRes.data || [], reviewAccounts, rules: savedRules, userId, cashCosts: cashRes.error ? [] : (cashRes.data || []).filter(r => r.job_id), offeredCategories: EXPENSE_CATEGORIES.map(c => c.label) });
        const ac = {}, vc = {};
        sum.accounts.forEach(a => { ac[a.name.toLowerCase()] = a.isLabor; });
        sum.vendors.forEach(v => { vc[v.name.toLowerCase()] = v.choice; });
        setRules(savedRules); setSummary(sum); setAcctChoice(ac); setVendorChoice(vc);
        setError(rulesRes.error ? "Labor choices can't be saved yet. Canopy is still being updated for this." : '');
      } catch (e) { // eslint-disable-line no-unused-vars
        if (!cancelled) setError("We couldn't load your costs. Please try again.");
      }
      if (!cancelled) setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, [userId]);

  async function handleSave() {
    if (!summary) return;
    setSaving(true); setError('');
    try {
      const acctPlan = planRuleChanges('account', rules, summary.accounts.map(a => ({ name: a.name, isLabor: acctChoice[a.name.toLowerCase()] })));
      const vendPlan = planRuleChanges('vendor', rules, summary.vendors.map(v => {
        const c = vendorChoice[v.name.toLowerCase()];
        return { name: v.name, isLabor: c === 'labor' ? true : c === 'not_labor' ? false : null };
      }));
      const deletes = [...acctPlan.deletes, ...vendPlan.deletes];
      const updates = [...acctPlan.updates, ...vendPlan.updates];
      const inserts = [...acctPlan.inserts, ...vendPlan.inserts].map(r => ({ ...r, contractor_id: userId }));
      let failed = false;
      if (deletes.length) {
        const { error: e } = await supabase.from('labor_rules').delete().eq('contractor_id', userId).in('id', deletes);
        if (e) failed = true;
      }
      for (const u of updates) {
        const { error: e } = await supabase.from('labor_rules').update({ is_labor: u.is_labor }).eq('contractor_id', userId).eq('id', u.id); // eslint-disable-line no-await-in-loop
        if (e) failed = true;
      }
      if (inserts.length) {
        const { error: e } = await supabase.from('labor_rules').insert(inserts);
        if (e) failed = true;
      }
      if (failed) { setError(LABOR_SAVE_ERROR); setSaving(false); return; }
      onSave();
    } catch (err) {
      console.error('Error saving labor choices');
      setError(LABOR_SAVE_ERROR);
    }
    setSaving(false);
  }

  const text = { fontFamily:"'DM Sans',sans-serif" };
  const row = { display:'flex',alignItems:'center',gap:12,padding:'10px 28px',borderBottom:`1px solid ${BORDER}` };
  const heading = { ...text,fontSize:13,fontWeight:600,color:DARK,padding:'16px 28px 4px' };
  const note = { ...text,fontSize:12,color:DIM,lineHeight:1.5,padding:'0 28px 8px' };

  return (
    <>
      <div style={{ flex:1,overflowY:'auto',padding:'4px 0 8px' }}>
        {loading ? (
          <div style={{ padding:'40px',textAlign:'center',...text,fontSize:13,color:DIM }}>Loading your costs...</div>
        ) : !summary ? null : (
          <>
            <p style={{ ...text,fontSize:13,color:MID,lineHeight:1.5,padding:'12px 28px 0' }}>
              Canopy counts these costs as labor on each job. Everything else counts as materials. Your profit and margin stay the same either way.
            </p>

            <div style={heading}>Categories that count as labor</div>
            <div style={note}>Includes categories you use when adding a cost in Canopy.</div>
            {summary.accounts.length === 0 ? (
              <div style={note}>{fromQuickBooks
                ? 'No categories yet. Canopy reads each cost\'s QuickBooks category the next time you sync. Until then, only the choices under "People and companies you pay for labor" apply.'
                : 'No categories yet. They appear here when you upload costs with a category or add a cost to a job in Canopy.'}</div>
            ) : summary.accounts.map(a => {
              const key = a.name.toLowerCase();
              const on = !!acctChoice[key];
              return (
                <label key={key} style={{ ...row,cursor:'pointer',background:on?'rgba(92,122,90,0.06)':'transparent' }}>
                  <input type="checkbox" checked={on} onChange={() => setAcctChoice(p => ({ ...p, [key]: !on }))} />
                  <span style={{ ...text,fontSize:13,color:on?DARK:MID,fontWeight:on?500:400,flex:1 }}>{a.name}</span>
                  <span style={{ ...text,fontSize:12,color:DIM }}>{a.cents ? formatMoney(a.cents / 100) : ''}</span>
                </label>
              );
            })}
            {summary.withoutCategoryCount > 0 && (
              <div style={{ ...note,paddingTop:8 }}>
                {formatMoney(summary.withoutCategoryCents / 100)} across {summary.withoutCategoryCount} cost{summary.withoutCategoryCount !== 1 ? 's' : ''} has no category yet, so it counts as materials unless you mark who was paid below.{fromQuickBooks ? ' Canopy reads categories the next time you sync.' : ''}
              </div>
            )}

            <div style={heading}>People and companies you pay for labor</div>
            <div style={note}>Your choice here beats the category above.</div>
            {summary.vendors.length === 0 ? (
              <div style={note}>No people or companies found yet.</div>
            ) : summary.vendors.map(v => {
              const key = v.name.toLowerCase();
              return (
                <div key={key} style={row}>
                  <span style={{ ...text,fontSize:13,color:DARK,flex:1 }}>{v.name}</span>
                  <span style={{ ...text,fontSize:12,color:DIM }}>{formatMoney(v.cents / 100)}</span>
                  <select
                    aria-label={`Is ${v.name} labor?`}
                    value={vendorChoice[key] || 'auto'}
                    onChange={e => setVendorChoice(p => ({ ...p, [key]: e.target.value }))}
                    style={{ ...text,fontSize:12,color:DARK,padding:'5px 8px',borderRadius:5,border:`1px solid ${BORDER}`,background:BG }}
                  >
                    <option value="auto">Follow the category</option>
                    <option value="labor">Labor</option>
                    <option value="not_labor">Not labor</option>
                  </select>
                </div>
              );
            })}
          </>
        )}
      </div>
      <div className="r-px r-wrap" style={{ padding:'16px 28px',borderTop:`1px solid ${BORDER}`,display:'flex',alignItems:'center',justifyContent:'space-between',gap:10 }}>
        <div role="alert" style={{ ...text,fontSize:12,color:error?'#B4432F':DIM }}>{error}</div>
        <div style={{ display:'flex',gap:10 }}>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn act" onClick={handleSave} disabled={saving || loading || !summary}
            style={{ opacity:(saving || loading || !summary) ? 0.5 : 1 }}>
            {saving ? 'Saving...' : 'Save labor choices'}
          </button>
        </div>
      </div>
    </>
  );
}

// Owner-facing check of how well auto-sorting would have done on expenses already sorted.
// The server reads the data and sends back counts only; nothing is saved.
function SortingPanel({ onClose }) {
  const [state, setState] = useState('idle'); // 'idle' | 'loading' | 'done' | 'error'
  const [data, setData]   = useState(null);
  const [message, setMessage] = useState('');

  async function runCheck() {
    setState('loading'); setMessage('');
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) { setMessage('Please sign in again to run this check.'); setState('error'); return; }
      const res = await fetch('/api/matching-bench', { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      const body = await res.json().catch(() => ({}));
      if (res.status === 401) { setMessage('Please sign in again to run this check.'); setState('error'); return; }
      if (!res.ok || !body.success) { setMessage(typeof body.error === 'string' ? body.error : 'We could not run the check. Please try again in a minute.'); setState('error'); return; }
      setData(body); setState('done');
    } catch (e) { // eslint-disable-line no-unused-vars
      setMessage('We could not run the check. Please try again in a minute.'); setState('error');
    }
  }

  const summary = state === 'done' ? describeSortingCheck(data) : null;
  const text = { fontFamily:"'DM Sans',sans-serif",fontSize:13,color:MID,lineHeight:1.6 };
  return (
    <>
      <div className="r-px" style={{ padding:'18px 28px',overflowY:'auto',flex:1 }}>
        <h3 style={{ fontFamily:"'Lora',serif",fontSize:16,fontWeight:500,color:DARK,marginBottom:6 }}>How well Canopy sorts your expenses</h3>
        <p style={{ ...text,color:DIM,marginBottom:14 }}>
          Canopy replays the expenses you have already sorted and guesses each one using only what came before it. Nothing is changed or saved.
        </p>
        {state === 'loading' && <p role="status" style={text}>Checking your expenses. This can take a few seconds.</p>}
        {state === 'error' && <p role="alert" style={{ ...text,color:'#B5483A' }}>{message}</p>}
        {state === 'done' && !summary && <p style={text}>You have no sorted expenses to check yet. Assign some expenses to jobs and try again.</p>}
        {summary && (
          <div style={text}>
            <p style={{ marginBottom:8 }}>{summary.intro}</p>
            {summary.lines.map(l => <p key={l}>{l}</p>)}
            <p style={{ marginTop:8,color:DIM }}>{summary.note}</p>
          </div>
        )}
      </div>
      <div className="r-px" style={{ padding:'16px 28px',borderTop:`1px solid ${BORDER}`,display:'flex',justifyContent:'flex-end',gap:10 }}>
        <button className="btn" onClick={onClose}>Close</button>
        <button className="btn act" onClick={runCheck} disabled={state === 'loading'} style={{ opacity: state === 'loading' ? 0.5 : 1 }}>
          {state === 'loading' ? 'Checking...' : 'Check now'}
        </button>
      </div>
    </>
  );
}


// ─── QUICKBOOKS CONNECTION ───────────────────────────────────────────────────
// When QuickBooks last synced, and the Disconnect QuickBooks section. Not shown in the demo.
const KEEP_NOTE = "Canopy stops syncing. Your jobs, income and costs stay as they are.";
const REMOVE_NOTE = "Canopy stops syncing and removes the jobs, income and costs that came from QuickBooks, your expenses to sort, and the QuickBooks expenses you assigned to jobs. Anything you uploaded or entered in Canopy stays, and so do your vendor and labor choices. If you reconnect later, QuickBooks numbers come back, but you'll need to sort expenses again.";
const REMOVE_ONLY_NOTE = "Canopy removes the jobs, income and costs that came from QuickBooks, your expenses to sort, and the QuickBooks expenses you assigned to jobs. Anything you uploaded or entered in Canopy stays, and so do your vendor and labor choices. If you reconnect later, QuickBooks numbers come back, but you'll need to sort expenses again.";
const REMOVE_ERROR = "We couldn't remove your QuickBooks data. Please try again.";
const REMOVE_SIGNIN = 'Please sign in again to remove QuickBooks data.';
const DISCONNECT_ERROR = "We couldn't disconnect QuickBooks. Please try again.";

function ConnectionPanel({ userId, onClose, syncing, connected = true }) {
  const [status, setStatus] = useState(null);      // { last_synced_at, last_sync_problems }
  const [step, setStep]     = useState('idle');    // 'idle' | 'confirm' | 'working' | 'error'
  const [message, setMessage] = useState('');
  const demo = isDemoPath(window.location.pathname);
  const removeOnly = !connected;

  useEffect(() => {
    if (demo) return undefined;
    let cancelled = false;
    (async () => {
      // The two newer columns exist only after the sync status update is applied: fall back without them.
      let res = await supabase.from('contractors').select('last_synced_at, last_sync_problems').eq('id', userId).single();
      if (res.error) res = await supabase.from('contractors').select('last_synced_at').eq('id', userId).single();
      if (!cancelled) setStatus(res.error ? {} : (res.data || {}));
    })();
    return () => { cancelled = true; };
  }, [userId, demo]);

  if (demo) return null;

  async function disconnect(removeData) {
    setStep('working'); setMessage('');
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) { setMessage(removeOnly ? REMOVE_SIGNIN : 'Please sign in again to disconnect QuickBooks.'); setStep('error'); return; }
      const res = await fetch('/api/qb-disconnect', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ removeData }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.status === 401) { setMessage(removeOnly ? REMOVE_SIGNIN : 'Please sign in again to disconnect QuickBooks.'); setStep('error'); return; }
      if (!res.ok || !body.success) { setMessage(removeOnly && !body.disconnected ? REMOVE_ERROR : typeof body.error === 'string' ? (body.disconnected ? body.error.replace(/ Please try again\.$/, '') + ' Keep this window open and choose it again.' : body.error) : DISCONNECT_ERROR); setStep('error'); return; }
      // Reload so the app shows QuickBooks as disconnected and loads the numbers that are left.
      window.location.assign('/?qb_disconnected=true');
    } catch (e) { // eslint-disable-line no-unused-vars
      setMessage(removeOnly ? REMOVE_ERROR : DISCONNECT_ERROR); setStep('error');
    }
  }

  const text = { fontFamily:"'DM Sans',sans-serif",fontSize:13,color:MID,lineHeight:1.6 };
  const problemLine = status ? syncProblemsText(status.last_sync_problems) : '';
  const busy = step === 'working' || !!syncing;   // never while a sync is running
  if (!connected) {
    // QuickBooks was disconnected earlier but its numbers are still here. The server accepts the removal with no saved connection.
    return (
      <>
        <div className="r-px" style={{ padding:'18px 28px',overflowY:'auto',flex:1 }}>
          <h3 style={{ fontFamily:"'Lora',serif",fontSize:16,fontWeight:500,color:DARK,marginBottom:6 }}>QuickBooks</h3>
          <p style={{ ...text,color:DARK }}>QuickBooks is not connected.</p>
          <p style={{ ...text,color:DIM,marginBottom:18 }}>Some jobs, income or costs from QuickBooks may still be in Canopy. Jobs that have labor, costs or income you added in Canopy are kept.</p>
          {step === 'idle' && <button className="btn red" onClick={() => setStep('confirm')}>Remove QuickBooks data</button>}
          {(step === 'confirm' || step === 'working' || step === 'error') && (
            <div role="group" aria-label="Remove QuickBooks data" style={{ border:`1px solid ${BORDER}`,borderRadius:6,padding:14 }}>
              <p style={{ ...text,color:DARK,marginBottom:12 }}>Remove the numbers that came from QuickBooks?</p>
              <p style={{ ...text,fontSize:12,color:DIM,marginBottom:14 }}>{REMOVE_ONLY_NOTE}</p>
              <button className="btn red" disabled={busy} onClick={() => disconnect(true)} style={{ width:'100%',marginBottom:10 }}>Remove QuickBooks data</button>
              {step === 'working' && <p role="status" style={text}>Removing...</p>}
              {step === 'error' && <p role="alert" style={{ ...text,color:'#B5483A' }}>{message}</p>}
              <button className="btn" disabled={step === 'working'} onClick={() => { setStep('idle'); setMessage(''); }}>Cancel</button>
            </div>
          )}
        </div>
        <div className="r-px" style={{ padding:'16px 28px',borderTop:`1px solid ${BORDER}`,display:'flex',justifyContent:'flex-end',gap:10 }}>
          <button className="btn" onClick={onClose}>Close</button>
        </div>
      </>
    );
  }
  return (
    <>
      <div className="r-px" style={{ padding:'18px 28px',overflowY:'auto',flex:1 }}>
        <h3 style={{ fontFamily:"'Lora',serif",fontSize:16,fontWeight:500,color:DARK,marginBottom:6 }}>Syncing</h3>
        <p style={{ ...text,color:DARK }}>{status ? lastSyncedText(status.last_synced_at) : 'Checking...'}</p>
        {problemLine && <p style={{ ...text,color:'#B5483A' }}>{problemLine}</p>}
        <p style={{ ...text,color:DIM,marginBottom:22 }}>Canopy syncs your QuickBooks data about once a day. You can also choose Sync QuickBooks now at any time.</p>

        <h3 style={{ fontFamily:"'Lora',serif",fontSize:16,fontWeight:500,color:DARK,marginBottom:6 }}>Disconnect QuickBooks</h3>
        {step === 'idle' && (
          <>
            <p style={{ ...text,color:DIM,marginBottom:12 }}>Stop Canopy from reading your QuickBooks account. You choose what happens to the numbers already in Canopy.</p>
            <button className="btn red" onClick={() => setStep('confirm')}>Disconnect QuickBooks</button>
          </>
        )}
        {(step === 'confirm' || step === 'working' || step === 'error') && (
          <div role="group" aria-label="Disconnect QuickBooks" style={{ border:`1px solid ${BORDER}`,borderRadius:6,padding:14 }}>
            <p style={{ ...text,color:DARK,marginBottom:12 }}>Choose what happens to your data.</p>
            <div style={{ marginBottom:14 }}>
              <button className="btn" disabled={busy} onClick={() => disconnect(false)} style={{ width:'100%',marginBottom:6 }}>Disconnect and keep your data</button>
              <p style={{ ...text,fontSize:12,color:DIM }}>{KEEP_NOTE}</p>
            </div>
            <div style={{ marginBottom:14 }}>
              <button className="btn red" disabled={busy} onClick={() => disconnect(true)} style={{ width:'100%',marginBottom:6 }}>Disconnect and remove QuickBooks data</button>
              <p style={{ ...text,fontSize:12,color:DIM }}>{REMOVE_NOTE}</p>
            </div>
            {syncing && <p role="status" style={text}>A sync is running. Wait for it to finish, then choose.</p>}
            {step === 'working' && <p role="status" style={text}>Disconnecting...</p>}
            {step === 'error' && <p role="alert" style={{ ...text,color:'#B5483A' }}>{message}</p>}
            <button className="btn" disabled={step === 'working'} onClick={() => { setStep('idle'); setMessage(''); }}>Cancel</button>
          </div>
        )}
      </div>
      <div className="r-px" style={{ padding:'16px 28px',borderTop:`1px solid ${BORDER}`,display:'flex',justifyContent:'flex-end',gap:10 }}>
        <button className="btn" onClick={onClose}>Close</button>
      </div>
    </>
  );
}

// `tabs`: [key, label] pairs from settingsTabs(). Left out, all four show. null means the choice is still loading.
// `qbConnected` only changes the Connection tab (connected: sync and disconnect; not connected: remove QuickBooks data).
function VendorSetup({ userId, vendorRules, onSave, onClose, isFirstRun, syncing, tabs, qbConnected = true, checkFailed = false }) {
  const [pickedTab, setTab]           = useState(null); // 'vendors' | 'labor' | 'sorting' | 'connection'
  const allTabs = [['vendors','Job vendors'],['labor','Labor'],['sorting','Sorting'],...(isDemoPath(window.location.pathname) ? [] : [['connection','Connection']])];
  const shownTabs = isFirstRun ? allTabs : (tabs === undefined ? allTabs : (tabs || []));
  const tab = shownTabs.some(([k]) => k === pickedTab) ? pickedTab : (shownTabs[0] ? shownTabs[0][0] : 'vendors');
  const tabsLoading = !isFirstRun && tabs === null;
  const hasVendorsTab = shownTabs.some(([k]) => k === 'vendors');
  const [allVendors, setAllVendors]   = useState([]);
  const [selected, setSelected]       = useState(new Set());
  const [search, setSearch]           = useState('');
  const [loading, setLoading]         = useState(true);
  const [loadFailed, setLoadFailed]   = useState(false);
  const [saving, setSaving]           = useState(false);

  // On mount: load all distinct vendors from synced data + pre-check already-tracked ones
  useEffect(() => {
    async function load() {
      setLoading(true);
      setLoadFailed(false);
      try {
        const [txnPage, inboxPage] = await Promise.all([
          fetchAllRows(() => supabase.from('transactions').select('vendor').eq('contractor_id', userId).not('vendor', 'is', null)),
          fetchAllRows(() => supabase.from('inbox_tags').select('vendor').eq('contractor_id', userId).not('vendor', 'is', null)),
        ]);
        // A failed page must not leave a cut-short vendor list that looks complete
        if (txnPage.error || inboxPage.error) throw (txnPage.error || inboxPage.error);
        const txnRows = txnPage.data, inboxRows = inboxPage.data;
        const vendorSet = new Set([
          ...(txnRows  || []).map(r => r.vendor),
          ...(inboxRows || []).map(r => r.vendor),
        ].filter(v => v && v.trim() !== '' && v !== 'Unknown Vendor'));
        const sorted = [...vendorSet].sort((a, b) => a.localeCompare(b));
        setAllVendors(sorted);

        // Pre-check vendors already marked 'tracked' in vendor_rules
        const alreadyTracked = new Set(
          (vendorRules || [])
            .filter(r => r.rule_type === 'tracked')
            .map(r => r.vendor_name)
        );
        setSelected(alreadyTracked);
      } catch (e) { // eslint-disable-line no-unused-vars
        console.error('Error loading vendors for setup');
        setLoadFailed(true);
      }
      setLoading(false);
    }
    load();
  }, [userId, vendorRules]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = allVendors.filter(v => v.toLowerCase().includes(search.toLowerCase()));
  const allFilteredSelected = filtered.length > 0 && filtered.every(v => selected.has(v));

  function toggleVendor(v) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(v)) next.delete(v); else next.add(v);
      return next;
    });
  }

  function toggleAllFiltered() {
    setSelected(prev => {
      const next = new Set(prev);
      if (allFilteredSelected) filtered.forEach(v => next.delete(v));
      else filtered.forEach(v => next.add(v));
      return next;
    });
  }

  async function handleSave() {
    setSaving(true);
    try {
      // Remove old 'tracked' rules and write fresh set
      await supabase
        .from('vendor_rules')
        .delete()
        .eq('contractor_id', userId)
        .eq('rule_type', 'tracked');

      if (selected.size > 0) {
        await supabase
          .from('vendor_rules')
          .insert([...selected].map(v => ({
            contractor_id: userId,
            vendor_name:   v,
            rule_type:     'tracked',
          })));
      }
      onSave();
    } catch (err) {
      console.error('Error saving vendor setup:', err);
    }
    setSaving(false);
  }

  return (
    <div className="r-overlay" style={{ position:'fixed',inset:0,background:'rgba(44,36,22,0.5)',zIndex:700,display:'flex',alignItems:'center',justifyContent:'center',padding:24 }}>
      <div className="r-sheet r-sheet-fixed" style={{ background:CARD,border:`1px solid ${BORDER}`,borderRadius:8,width:'100%',maxWidth:560,maxHeight:'88vh',display:'flex',flexDirection:'column',boxShadow:'0 24px 64px rgba(44,36,22,0.22)' }}>

        {/* Header */}
        <div className="r-px" style={{ padding:'24px 28px 18px',borderBottom:`1px solid ${BORDER}` }}>
          <div style={{ display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:12 }}>
            <div>
              <h2 style={{ fontFamily:"'Lora',serif",fontSize:20,fontWeight:500,color:DARK,letterSpacing:'-0.01em',marginBottom:4 }}>
                {isFirstRun ? 'Set up your job vendors' : tabsLoading ? 'Settings' : tab === 'labor' ? 'What counts as labor' : tab === 'sorting' ? 'Automatic sorting' : tab === 'connection' ? (qbConnected ? 'QuickBooks connection' : 'QuickBooks data') : 'Manage job vendors'}
              </h2>
              {(isFirstRun || (tab === 'vendors' && !tabsLoading)) && <p style={{ fontFamily:"'DM Sans',sans-serif",fontSize:13,color:DIM,lineHeight:1.5 }}>
                Select the vendors whose expenses should flow into your dashboard. Only these vendors will be synced — bank transactions and overhead vendors stay out.
              </p>}
            </div>
            {!isFirstRun && (
              <button className="r-close" aria-label="Close" onClick={onClose} style={{ background:'none',border:'none',cursor:'pointer',color:DIM,fontSize:20,lineHeight:1,padding:'2px 4px',flexShrink:0 }}>×</button>
            )}
          </div>
          {isFirstRun && (
            <div style={{ marginTop:14,padding:'11px 14px',borderRadius:5,background:'rgba(92,122,90,0.08)',border:`1px solid rgba(92,122,90,0.2)` }}>
              <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:12,color:ACCENT2,fontWeight:500,marginBottom:2 }}>You can change this any time</div>
              <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:12,color:MID }}>Open Settings (the gear in the sidebar, or More on a phone). Add or remove vendors whenever your business changes.</div>
            </div>
          )}
          {!isFirstRun && (
            <div role="tablist" style={{ display:'flex',flexWrap:'wrap',gap:8,marginTop:14 }}>
              {shownTabs.map(([k, label]) => (
                <button key={k} role="tab" aria-selected={tab === k} className={tab === k ? 'btn act' : 'btn'} onClick={() => setTab(k)} style={{ fontSize:12 }}>{label}</button>
              ))}
            </div>
          )}
        </div>

        {checkFailed && !isFirstRun && <p role="alert" style={{ fontFamily:"'DM Sans',sans-serif",fontSize:12,color:'#B5483A',padding:'10px 28px 0',margin:0 }}>We couldn't check for QuickBooks data. Close and open Settings to try again.</p>}
        {tabsLoading ? <div role="status" style={{ padding:'40px',textAlign:'center',fontFamily:"'DM Sans',sans-serif",fontSize:13,color:DIM }}>Loading your settings...</div> : tab === 'labor' && !isFirstRun ? <LaborPanel userId={userId} onSave={onSave} onClose={onClose} fromQuickBooks={qbConnected || hasVendorsTab} /> : tab === 'sorting' && !isFirstRun ? <SortingPanel onClose={onClose} /> : tab === 'connection' && !isFirstRun ? <ConnectionPanel userId={userId} onClose={onClose} syncing={syncing} connected={qbConnected} /> : (<>
        {/* Search + select-all */}
        <div className="r-px" style={{ padding:'14px 28px',borderBottom:`1px solid ${BORDER}`,display:'flex',alignItems:'center',gap:10 }}>
          <input
            type="text"
            placeholder="Search vendors..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ flex:1,padding:'7px 12px',borderRadius:5,border:`1px solid ${BORDER}`,background:BG,fontFamily:"'DM Sans',sans-serif",fontSize:12,color:DARK,outline:'none' }}
          />
          <button
            className="btn"
            onClick={toggleAllFiltered}
            style={{ whiteSpace:'nowrap',fontSize:11 }}
          >
            {allFilteredSelected ? 'Deselect all' : 'Select all'}
          </button>
        </div>

        {/* Vendor list */}
        <div style={{ flex:1,overflowY:'auto',padding:'8px 0' }}>
          {loading ? (
            <div style={{ padding:'40px',textAlign:'center',fontFamily:"'DM Sans',sans-serif",fontSize:13,color:DIM }}>Loading vendors...</div>
          ) : loadFailed ? (
            <div style={{ padding:'40px',textAlign:'center',fontFamily:"'DM Sans',sans-serif",fontSize:13,color:DIM }}>We couldn't load your vendors. Please close this and try again.</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding:'40px',textAlign:'center',fontFamily:"'DM Sans',sans-serif",fontSize:13,color:DIM }}>
              {allVendors.length === 0 ? 'No vendors yet. They appear here once QuickBooks has synced.' : 'No vendors match your search.'}
            </div>
          ) : (
            filtered.map(v => {
              const isChecked = selected.has(v);
              return (
                <div
                  key={v}
                  onClick={() => toggleVendor(v)}
                  className="r-px" style={{ display:'flex',alignItems:'center',gap:12,padding:'10px 28px',cursor:'pointer',background:isChecked?'rgba(92,122,90,0.06)':'transparent',borderBottom:`1px solid ${BORDER}`,transition:'background 0.1s' }}
                >
                  <div style={{ width:16,height:16,borderRadius:3,border:`1.5px solid ${isChecked?ACCENT2:BORDER}`,background:isChecked?ACCENT2:'transparent',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,transition:'all 0.15s' }}>
                    {isChecked && <span style={{ color:CARD,fontSize:11,lineHeight:1,fontWeight:700 }}>✓</span>}
                  </div>
                  <span style={{ fontFamily:"'DM Sans',sans-serif",fontSize:13,color:isChecked?DARK:MID,fontWeight:isChecked?500:400 }}>{v}</span>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="r-px r-wrap" style={{ padding:'16px 28px',borderTop:`1px solid ${BORDER}`,display:'flex',alignItems:'center',justifyContent:'space-between',gap:10 }}>
          <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:12,color:DIM }}>
            {selected.size} vendor{selected.size !== 1 ? 's' : ''} selected
          </div>
          <div style={{ display:'flex',gap:10 }}>
            {!isFirstRun && <button className="btn" onClick={onClose}>Cancel</button>}
            <button
              className="btn act"
              onClick={handleSave}
              disabled={saving || selected.size === 0}
              style={{ opacity:(saving || selected.size === 0) ? 0.5 : 1 }}
            >
              {saving ? 'Saving...' : isFirstRun ? 'Save and resync →' : 'Save changes'}
            </button>
          </div>
        </div>
        </>)}

      </div>
    </div>
  );
}

export { VendorSetup };
